import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CurrencyDollar, TrendUp, ChartLine } from "@phosphor-icons/react";
import api, { formatApiError } from "../../lib/api";
import { formatCurrency } from "../../lib/currency";

const fmt = (n, cur = "USD") => formatCurrency(n, cur);

function Sparkline({ series }) {
  if (!series?.length) return null;
  const values = series.map((s) => s.net);
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = Math.max(max - min, 1);
  const W = 240;
  const H = 44;
  const step = W / Math.max(series.length - 1, 1);
  const path = values
    .map((v, i) => `${i === 0 ? "M" : "L"}${(i * step).toFixed(1)},${(H - ((v - min) / range) * H).toFixed(1)}`)
    .join(" ");
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} data-testid="revenue-sparkline">
      <path d={path} fill="none" stroke="#FF3E00" strokeWidth="2" />
        {values.map((v, i) => (
          <circle key={`sp-${i}-${v}`} cx={(i * step).toFixed(1)} cy={(H - ((v - min) / range) * H).toFixed(1)} r="1.5" fill="#09090b" />
        ))}
    </svg>
  );
}

export default function RevenueWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api
      .get("/platform/revenue-summary?months=12")
      .then((r) => { if (mounted) setData(r.data); })
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail) || "Failed to load revenue summary"))
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return (
      <div className="mt-6 border border-zinc-950 p-4 font-mono text-xs text-zinc-500" data-testid="revenue-widget-loading">
        Loading revenue…
      </div>
    );
  }
  if (!data) return null;

  const t = data.totals || { paid: 0, refunded: 0, net: 0 };
  const cur = data.primary_currency || "USD";
  const others = (data.totals_by_currency || []).filter((x) => x.currency !== cur);

  return (
    <section
      className="mt-6 border border-zinc-950"
      data-testid="revenue-widget"
    >
      <div className="grid grid-cols-1 md:grid-cols-5 divide-x divide-zinc-200">
        <div className="p-4 md:col-span-1 border-b md:border-b-0 border-zinc-200">
          <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-zinc-500 flex items-center gap-1">
            <CurrencyDollar size={11} weight="bold" /> Paid 12m
          </div>
          <div className="font-display font-black text-2xl tnum mt-1" data-testid="revenue-paid">{fmt(t.paid, cur)}</div>
          <div className="text-[10px] font-mono text-zinc-400 mt-0.5 tnum">{data.charge_count} charges</div>
        </div>
        <div className="p-4">
          <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-zinc-500">Refunded</div>
          <div className="font-display font-black text-2xl tnum mt-1 text-red-700" data-testid="revenue-refunded">{fmt(t.refunded, cur)}</div>
          <div className="text-[10px] font-mono text-zinc-400 mt-0.5 tnum">{data.refund_count} refunds</div>
        </div>
        <div className="p-4">
          <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-zinc-500">Net</div>
          <div className="font-display font-black text-2xl tnum mt-1 text-[#FF3E00]" data-testid="revenue-net">{fmt(t.net, cur)}</div>
          <div className="text-[10px] font-mono text-zinc-400 mt-0.5 tnum">{data.paying_tenants} paying tenants</div>
        </div>
        <div className="p-4">
          <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-zinc-500 flex items-center gap-1">
            <TrendUp size={11} weight="bold" /> MRR (last month)
          </div>
          <div className="font-display font-black text-2xl tnum mt-1" data-testid="revenue-mrr">{fmt(data.mrr_estimate, cur)}</div>
        </div>
        <div className="p-4 md:col-span-1">
          <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-zinc-500 flex items-center gap-1">
            <ChartLine size={11} weight="bold" /> 12-month trend
          </div>
          <div className="mt-1">
            {data.series?.length > 0 ? <Sparkline series={data.series} /> : <div className="text-xs text-zinc-400 mt-2">No data yet</div>}
          </div>
        </div>
      </div>

      {others.length > 0 && (
        <div className="border-t border-zinc-200 px-4 py-2 bg-zinc-50 flex flex-wrap gap-3 text-[11px] font-mono text-zinc-600" data-testid="revenue-per-currency">
          <span className="text-zinc-400 tracking-[0.18em] uppercase text-[10px]">Also in:</span>
          {others.map((o) => (
            <span key={o.currency} data-testid={`revenue-currency-${o.currency}`}>
              {formatCurrency(o.net, o.currency)}
            </span>
          ))}
        </div>
      )}
    </section>
  );
}
